import React, { useState } from "react";
import { Template, Section, Question, QuestionType } from "./types";
import { newId } from "./siteCheckStore";

interface Props {
  template: Template;
  onChange: (next: Template) => void;
  onClose: () => void;
}

// Single-template editor: sections at the top, questions inside each
// section. Reorder is up/down arrows (no drag-drop) — keeps the touch
// target large for tablet use during template setup. All edits flush
// to the parent immediately via onChange so the modal can be closed
// at any time without a save step.
export default function SiteCheckTemplateEditor({ template, onChange, onClose }: Props) {
  const [name, setName] = useState(template.name);

  const flush = (next: Partial<Template>) => {
    onChange({ ...template, ...next, updatedAt: new Date().toISOString() });
  };

  const updateName = (v: string) => {
    setName(v);
    flush({ name: v });
  };

  // ── Section ops ─────────────────────────────────────────────────────────

  const addSection = () => {
    const sec: Section = {
      id: newId("sec"),
      label: `Section ${template.sections.length + 1}`,
      questions: [],
    };
    flush({ sections: [...template.sections, sec] });
  };

  const updateSection = (id: string, patch: Partial<Section>) => {
    flush({
      sections: template.sections.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    });
  };

  const removeSection = (id: string) => {
    if (!window.confirm("Delete this section and all its questions?")) return;
    flush({ sections: template.sections.filter((s) => s.id !== id) });
  };

  const moveSection = (id: string, dir: -1 | 1) => {
    const idx = template.sections.findIndex((s) => s.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= template.sections.length) return;
    const next = template.sections.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    flush({ sections: next });
  };

  // ── Question ops ────────────────────────────────────────────────────────

  const addQuestion = (sectionId: string, type: QuestionType) => {
    const sec = template.sections.find((s) => s.id === sectionId);
    if (!sec) return;
    const q: Question = {
      key: newId("q"),
      label: type === "photo" ? "Photo" : type === "yesNo" ? "Yes / No" : "Note",
      type,
      required: false,
    };
    updateSection(sectionId, { questions: [...sec.questions, q] });
  };

  const updateQuestion = (sectionId: string, qKey: string, patch: Partial<Question>) => {
    const sec = template.sections.find((s) => s.id === sectionId);
    if (!sec) return;
    updateSection(sectionId, {
      questions: sec.questions.map((q) => (q.key === qKey ? { ...q, ...patch } : q)),
    });
  };

  const removeQuestion = (sectionId: string, qKey: string) => {
    const sec = template.sections.find((s) => s.id === sectionId);
    if (!sec) return;
    updateSection(sectionId, {
      questions: sec.questions.filter((q) => q.key !== qKey),
    });
  };

  const moveQuestion = (sectionId: string, qKey: string, dir: -1 | 1) => {
    const sec = template.sections.find((s) => s.id === sectionId);
    if (!sec) return;
    const idx = sec.questions.findIndex((q) => q.key === qKey);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= sec.questions.length) return;
    const qs = sec.questions.slice();
    [qs[idx], qs[target]] = [qs[target], qs[idx]];
    updateSection(sectionId, { questions: qs });
  };

  return (
    <div className="sc-modal-overlay">
      <div className="sc-modal sc-modal-wide">
        <div className="sc-modal-header">
          <h2>Template Editor</h2>
          <button className="sc-btn-cancel" onClick={onClose}>Done</button>
        </div>

        <div className="sc-modal-body">
          <label className="sc-field">
            <span>Template Name</span>
            <input
              className="sc-input"
              value={name}
              onChange={(e) => updateName(e.target.value)}
              placeholder="e.g. Site Inspection v1"
            />
          </label>

          <div className="sc-sections">
            {template.sections.map((sec, sIdx) => (
              <div key={sec.id} className="sc-section">
                <div className="sc-section-head">
                  <input
                    className="sc-input sc-section-name"
                    value={sec.label}
                    onChange={(e) => updateSection(sec.id, { label: e.target.value })}
                  />
                  <div className="sc-section-actions">
                    <button
                      className="sc-icon-btn"
                      onClick={() => moveSection(sec.id, -1)}
                      disabled={sIdx === 0}
                      title="Move up"
                    >▲</button>
                    <button
                      className="sc-icon-btn"
                      onClick={() => moveSection(sec.id, 1)}
                      disabled={sIdx === template.sections.length - 1}
                      title="Move down"
                    >▼</button>
                    <button
                      className="sc-icon-btn sc-icon-danger"
                      onClick={() => removeSection(sec.id)}
                      title="Delete section"
                    >✕</button>
                  </div>
                </div>

                <div className="sc-questions">
                  {sec.questions.map((q, qIdx) => (
                    <div key={q.key} className="sc-question">
                      <select
                        className="sc-input sc-q-type"
                        value={q.type}
                        onChange={(e) =>
                          updateQuestion(sec.id, q.key, { type: e.target.value as QuestionType })
                        }
                      >
                        <option value="photo">Photo</option>
                        <option value="text">Text</option>
                        <option value="yesNo">Yes / No</option>
                      </select>
                      <input
                        className="sc-input sc-q-label"
                        value={q.label}
                        onChange={(e) => updateQuestion(sec.id, q.key, { label: e.target.value })}
                      />
                      <label className="sc-q-required">
                        <input
                          type="checkbox"
                          checked={!!q.required}
                          onChange={(e) =>
                            updateQuestion(sec.id, q.key, { required: e.target.checked })
                          }
                        />
                        Required
                      </label>
                      <button
                        className="sc-icon-btn"
                        onClick={() => moveQuestion(sec.id, q.key, -1)}
                        disabled={qIdx === 0}
                        title="Move up"
                      >▲</button>
                      <button
                        className="sc-icon-btn"
                        onClick={() => moveQuestion(sec.id, q.key, 1)}
                        disabled={qIdx === sec.questions.length - 1}
                        title="Move down"
                      >▼</button>
                      <button
                        className="sc-icon-btn sc-icon-danger"
                        onClick={() => removeQuestion(sec.id, q.key)}
                        title="Delete"
                      >✕</button>
                    </div>
                  ))}

                  <div className="sc-add-question">
                    <button className="sc-btn-secondary" onClick={() => addQuestion(sec.id, "photo")}>
                      + Photo
                    </button>
                    <button className="sc-btn-secondary" onClick={() => addQuestion(sec.id, "text")}>
                      + Text
                    </button>
                    <button className="sc-btn-secondary" onClick={() => addQuestion(sec.id, "yesNo")}>
                      + Yes/No
                    </button>
                  </div>
                </div>
              </div>
            ))}

            <button className="sc-btn-primary sc-add-section" onClick={addSection}>
              + Add Section
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
